import {
  AppstoreOutlined,
  FilterOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-components';
import { Badge, Button, Card, Drawer, Form, Segmented, Space } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';

export type ListViewMode = 'card' | 'table';

type AppListPageProps = {
  title: string;
  actions?: React.ReactNode;
  /** 筛选表单项（Form.Item）。手机端进右侧抽屉；PC 端展示在表格上方 */
  filterForm?: React.ReactNode;
  filterInitialValues?: Record<string, any>;
  filterValues?: Record<string, any>;
  onFilterSubmit?: (values: Record<string, any>) => void;
  onFilterReset?: () => void;
  /** 媒体库等固定卡片布局时可隐藏切换 */
  hideViewModeToggle?: boolean;
  viewMode?: ListViewMode;
  onViewModeChange?: (mode: ListViewMode) => void;
  children: React.ReactNode;
};

function countActiveFilters(values?: Record<string, any>) {
  if (!values) return 0;
  return Object.values(values).filter((v) => {
    if (v === undefined || v === null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  }).length;
}

/**
 * 列表页外壳：
 * - 移动端：标题栏右侧筛选按钮 → 右侧抽屉
 * - PC：筛选表单在内容区（表格/卡片）上方
 */
const AppListPage: React.FC<AppListPageProps> = ({
  title,
  actions,
  filterForm,
  filterInitialValues,
  filterValues,
  onFilterSubmit,
  onFilterReset,
  hideViewModeToggle = false,
  viewMode = 'table',
  onViewModeChange,
  children,
}) => {
  const isMobile = useIsMobile();
  const [filterOpen, setFilterOpen] = useState(false);
  const [form] = Form.useForm();
  const activeCount = useMemo(
    () => countActiveFilters(filterValues),
    [filterValues],
  );

  useEffect(() => {
    form.setFieldsValue(filterValues || filterInitialValues || {});
  }, [filterValues, filterInitialValues, form]);

  const applyFilters = async () => {
    const values = await form.validateFields();
    onFilterSubmit?.(values);
    setFilterOpen(false);
  };

  const resetFilters = () => {
    form.resetFields();
    onFilterReset?.();
    onFilterSubmit?.({});
    setFilterOpen(false);
  };

  const viewToggle =
    !hideViewModeToggle && onViewModeChange ? (
      <Segmented
        size="middle"
        value={viewMode}
        onChange={(v) => onViewModeChange(v as ListViewMode)}
        options={[
          {
            value: 'card',
            icon: <AppstoreOutlined />,
            label: isMobile ? undefined : '卡片',
          },
          {
            value: 'table',
            icon: <UnorderedListOutlined />,
            label: isMobile ? undefined : '列表',
          },
        ]}
      />
    ) : null;

  const filterBtn =
    filterForm && isMobile ? (
      <Badge dot={activeCount > 0} offset={[-2, 2]}>
        <Button
          type={activeCount > 0 ? 'primary' : 'text'}
          icon={<FilterOutlined />}
          onClick={() => setFilterOpen(true)}
          aria-label="筛选"
        />
      </Badge>
    ) : null;

  const headerRight = (
    <Space size={8} wrap={false}>
      {viewToggle}
      {filterBtn}
      {actions}
    </Space>
  );

  return (
    <PageContainer
      title={isMobile ? false : title}
      subTitle={false}
      breadcrumbRender={false}
      pageHeaderRender={isMobile ? false : undefined}
      extra={isMobile ? undefined : headerRight}
    >
      {isMobile && (
        <div className="app-page-bar">
          <div className="app-page-bar-title">{title}</div>
          <div className="app-page-bar-actions">{headerRight}</div>
        </div>
      )}

      {!isMobile && filterForm ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          <Form
            form={form}
            layout="vertical"
            initialValues={filterInitialValues}
            onFinish={async () => {
              await applyFilters();
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                alignItems: 'flex-end',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  flex: 1,
                  minWidth: 0,
                }}
                className="app-list-filter-inline"
              >
                {filterForm}
              </div>
              <Space style={{ marginBottom: 0 }}>
                <Button onClick={resetFilters}>重置</Button>
                <Button type="primary" htmlType="submit">
                  查询
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      ) : null}

      <div className={isMobile ? 'app-list-body' : undefined}>{children}</div>

      {filterForm && isMobile ? (
        <Drawer
          title="筛选"
          placement="right"
          width="86%"
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          destroyOnClose={false}
          extra={
            <Space>
              <Button onClick={resetFilters}>重置</Button>
              <Button type="primary" onClick={applyFilters}>
                应用
              </Button>
            </Space>
          }
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={filterInitialValues}
          >
            {filterForm}
          </Form>
        </Drawer>
      ) : null}
    </PageContainer>
  );
};

export function useListViewMode(): [
  ListViewMode,
  (mode: ListViewMode) => void,
  boolean,
] {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<ListViewMode>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'card' : 'table',
  );
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    if (!bootstrapped) {
      setMode(isMobile ? 'card' : 'table');
      setBootstrapped(true);
    }
  }, [isMobile, bootstrapped]);

  return [mode, setMode, isMobile];
}

export default AppListPage;
